import { useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { signOut } from 'aws-amplify/auth'
import { undoManager } from '@/services/undoManager'
import { secureLogger } from '@/lib/secure-logger'
import { Loader2 } from 'lucide-react'

/**
 * Public Logout Route
 *
 * Direct URL: https://monutrack.app/logout
 *
 * This route handles complete user logout by:
 * 1. Signing out from AWS Cognito (local only)
 * 2. Clearing undo manager state
 * 3. Clearing all localStorage data
 * 4. Clearing sessionStorage
 * 5. Redirecting to login page
 *
 * Query Parameters:
 * - redirectTo: (optional) URL to redirect to after logout (validated against allowlist)
 * - reason: (optional) Reason for logout (logged_out, session_expired, security, etc.)
 *
 * Examples:
 * - /logout
 * - /logout?reason=session_expired
 * - /logout?redirectTo=/login&reason=security
 */

interface LogoutSearch {
  redirectTo?: string
  reason?: 'logged_out' | 'session_expired' | 'security' | 'admin_action' | 'user_action'
}

export const Route = createFileRoute('/_public/logout')({
  validateSearch: (search: Record<string, unknown>): LogoutSearch => {
    return {
      redirectTo: typeof search.redirectTo === 'string' ? search.redirectTo : undefined,
      reason: (search.reason === 'logged_out' ||
               search.reason === 'session_expired' ||
               search.reason === 'security' ||
               search.reason === 'admin_action' ||
               search.reason === 'user_action')
        ? search.reason
        : 'logged_out'
    }
  },
  component: LogoutPage,
})

function LogoutPage() {
  const navigate = useNavigate()
  const { redirectTo, reason } = Route.useSearch()

  useEffect(() => {
    const performLogout = async () => {
      try {
        // STEP 1: Sign out from AWS Cognito (local only - doesn't sign out from other devices)
        // This automatically revokes the refresh token for this session
        secureLogger.info('User logout initiated', { reason })

        try {
          await signOut()
        } catch (signOutError) {
          // Even if Cognito signOut fails (network error, etc.), continue with local cleanup
          secureLogger.error('Cognito signOut failed, continuing with local cleanup', {
            error: signOutError instanceof Error ? signOutError.message : 'Unknown error'
          })
        }

        // STEP 2: Clear undo manager state
        if (undoManager.isInitialized()) {
          undoManager.reset()
          secureLogger.debug('Undo manager cleared')
        }

        // STEP 3: Clear ALL localStorage keys
        // This includes user-profile, role-override, tenant-override, and any other app data
        const keysToRemove = [
          'user-profile',
          'role-override',
          'tenant-override',
          // Add any other localStorage keys your app uses
          // Example: 'privacy-settings', 'ui-preferences', etc.
        ]

        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key)
          } catch (err) {
            // Silently fail if localStorage is not available (private browsing, etc.)
            secureLogger.warn(`Failed to remove localStorage key: ${key}`, {
              error: err instanceof Error ? err.message : 'Unknown error'
            })
          }
        })

        secureLogger.debug('localStorage cleared')

        // STEP 4: Clear sessionStorage (if your app uses it)
        try {
          sessionStorage.clear()
          secureLogger.debug('sessionStorage cleared')
        } catch (err) {
          // Silently fail if sessionStorage is not available
          secureLogger.warn('Failed to clear sessionStorage', {
            error: err instanceof Error ? err.message : 'Unknown error'
          })
        }

        // STEP 5: Validate redirect target against allowlist
        const allowedRedirects = ['/login', '/']
        const finalRedirectTo = redirectTo && allowedRedirects.includes(redirectTo)
          ? redirectTo
          : '/login'

        // STEP 6: Build redirect URL with logout reason
        const redirectUrl = `${finalRedirectTo}?reason=${reason || 'logged_out'}`

        secureLogger.info('Logout complete, redirecting', { redirectUrl })

        // STEP 7: Force full page redirect to clear any remaining state
        // Using window.location.href instead of navigate() ensures complete cleanup
        window.location.href = redirectUrl

      } catch (error) {
        // Catastrophic failure - log error and redirect anyway
        secureLogger.error('Unexpected error during logout', {
          error: error instanceof Error ? error.message : 'Unknown error'
        })

        // Even if everything fails, force redirect to login
        window.location.href = '/login?reason=logout_error'
      }
    }

    // Execute logout immediately on component mount
    performLogout()
  }, [redirectTo, reason, navigate])

  // Show loading spinner while logout is in progress
  // This will only be visible for a fraction of a second
  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-brand-primary animate-spin mx-auto mb-4" />
        <p className="text-gray-300 text-lg font-display">Saindo...</p>
        <p className="text-gray-500 text-sm mt-2 font-body">Você será redirecionado em instantes</p>
      </div>
    </div>
  )
}
